import { c as _c } from "react/compiler-runtime";
import { BadRequest, useTimeout } from '@systemeio/util';
import { updateUserPlan } from 'modules/profile/user-plan/api/user-plan-api';
import UpgradePlanConfirmationModal from 'modules/profile/user-plan/components/price-plan-columns/components/upgrade-plan-confirmation-modal';
import { BillingPeriodEnum } from 'modules/profile/user-plan/enums/billing-period-enum';
import { useRazorpayUserPlanCheckout } from 'modules/profile/user-plan/hooks/use-razorpay-user-plan-checkout';
import { useUserPlan } from 'modules/profile/user-plan/hooks/use-user-plan';
import { useUserPlanUpgradePolling } from 'modules/profile/user-plan/hooks/use-user-plan-upgrade-polling';
import { UserPlanInterface } from 'modules/profile/user-plan/types/user-plan-interface';
import { useEffect, useState } from 'react';
import { Button, Loader, toast } from 'shared/components';
import { useLocoTranslation } from 'shared/hooks/use-loco-translation';
export interface RazorpayUpgradePlanModalProps {
  pricePlan: UserPlanInterface;
  billingPeriod: BillingPeriodEnum;
  paymentPageUrl: string;
  cost: string;
  isPlanUpgradeAvailable?: boolean;
}
type RazorpayUpgradeStatus = 'idle' | 'submitting' | 'awaitingRazorpay' | 'awaitingWebhook';

// Display only — doesn't stop the poll in useUserPlanUpgradePolling, so a
// webhook that lands after this still gets picked up and confirmed.
const WAITING_TIMEOUT_MS = 30000;
const RazorpayUpgradePlanModal = t0 => {
  const $ = _c(33);
  const {
    pricePlan,
    billingPeriod,
    paymentPageUrl,
    cost,
    isPlanUpgradeAvailable
  } = t0;
  const {
    t
  } = useLocoTranslation();
  const [opened, setOpened] = useState(false);
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");
  const {
    mutate
  } = useUserPlan();
  const isFetching = status === "submitting" || status === "awaitingRazorpay";
  const t1 = status === "awaitingWebhook";
  let t2;
  if ($[0] !== pricePlan.id || $[1] !== t1) {
    t2 = {
      targetPlanId: pricePlan.id,
      allowFetch: t1
    };
    $[0] = pricePlan.id;
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const {
    isUpgradeConfirmed,
    mutate: mutatePolling
  } = useUserPlanUpgradePolling(t2);
  const timedOut = useTimeout(WAITING_TIMEOUT_MS, status === "awaitingWebhook");
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      onAuthorized: () => setStatus("awaitingWebhook"),
      onDismiss: () => setStatus("idle"),
      onFailed: message => {
        setStatus("idle");
        setError(message);
      }
    };
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const {
    openCheckout
  } = useRazorpayUserPlanCheckout(t3);
  let t4;
  let t5;
  if ($[4] !== isUpgradeConfirmed || $[5] !== mutate || $[6] !== mutatePolling || $[7] !== status || $[8] !== t) {
    t4 = () => {
      if (status !== "awaitingWebhook" || !isUpgradeConfirmed) {
        return;
      }
      mutate();
      mutatePolling();
      toast.success(t("dashboard.settings.user_plan.upgrade_success_title"));
      setStatus("idle");
      setOpened(false);
    };
    t5 = [status, isUpgradeConfirmed, mutate, mutatePolling, t];
    $[4] = isUpgradeConfirmed;
    $[5] = mutate;
    $[6] = mutatePolling;
    $[7] = status;
    $[8] = t;
    $[9] = t4;
    $[10] = t5;
  } else {
    t4 = $[9];
    t5 = $[10];
  }
  useEffect(t4, t5);
  let t6;
  if ($[11] !== billingPeriod || $[12] !== openCheckout || $[13] !== pricePlan.id || $[14] !== t) {
    t6 = async () => {
      setError("");
      setStatus("submitting");
      ;
      try {
        const response = await updateUserPlan({
          userPlan: pricePlan.id,
          period: billingPeriod
        });
        if (!response.data.razorpayResponse) {
          setStatus("idle");
          setError(t("core.error.internal_error_message"));
          return;
        }
        setStatus("awaitingRazorpay");
        try {
          await openCheckout(response.data.razorpayResponse);
        } catch {
          setStatus("idle");
          setError(t("dashboard.settings.user_plan.razorpay_script_load_error"));
        }
      } catch (t7) {
        const e = t7;
        setStatus("idle");
        if (e instanceof BadRequest && e.errors.common) {
          setError(e.errors.common.join(""));
        } else {
          setError(t("core.error.internal_error_message"));
        }
      }
    };
    $[11] = billingPeriod;
    $[12] = openCheckout;
    $[13] = pricePlan.id;
    $[14] = t;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  const onConfirm = t6;
  let t7;
  let t8;
  let t9;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => setOpened(true);
    t8 = () => setOpened(false);
    t9 = () => setError("");
    $[16] = t7;
    $[17] = t8;
    $[18] = t9;
  } else {
    t7 = $[16];
    t8 = $[17];
    t9 = $[18];
  }
  let t10;
  if ($[19] !== status || $[20] !== t || $[21] !== timedOut) {
    t10 = status === "awaitingWebhook" ? <div className="flex flex-col items-center justify-center gap-4 py-4"><Loader /><div className="text-center">{timedOut ? t("dashboard.settings.user_plan.razorpay_waiting_timeout_text") : t("dashboard.settings.user_plan.razorpay_waiting_text")}</div><Button variant="secondary" size="md" onClick={() => setOpened(false)}>{t("global.close")}</Button></div> : undefined;
    $[19] = status;
    $[20] = t;
    $[21] = timedOut;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== cost || $[24] !== error || $[25] !== isFetching || $[26] !== isPlanUpgradeAvailable || $[27] !== onConfirm || $[28] !== opened || $[29] !== paymentPageUrl || $[30] !== pricePlan || $[31] !== t10) {
    t11 = <UpgradePlanConfirmationModal pricePlan={pricePlan} paymentPageUrl={paymentPageUrl} cost={cost} isPlanUpgradeAvailable={isPlanUpgradeAvailable} opened={opened} onOpen={t7} onClose={t8} afterModalClose={t9} isFetching={isFetching} error={error} onConfirm={onConfirm} content={t10} />;
    $[23] = cost;
    $[24] = error;
    $[25] = isFetching;
    $[26] = isPlanUpgradeAvailable;
    $[27] = onConfirm;
    $[28] = opened;
    $[29] = paymentPageUrl;
    $[30] = pricePlan;
    $[31] = t10;
    $[32] = t11;
  } else {
    t11 = $[32];
  }
  return t11;
};
export default RazorpayUpgradePlanModal;